/**
 * On-Site Study Report — sample-style layout (v1).
 *
 * Mirrors the visual grammar of the reference doc
 * `VF_Desktop_Study_v2_Final.pdf` shipped at the repo root: navy/yellow
 * cover banner, green section headers with a yellow underline, navy table
 * headers with alternating rows, KPI card rows, and the five colour-coded
 * callout-box variants the sample uses (SIMULATED DATA, KEY INSIGHT,
 * METHOD NOTE, OPPORTUNITY, KEY FINDING).
 *
 * The DOM is captured by html2canvas via `generateBrandedPdf`, so the
 * on-screen view and the downloadable PDF share this exact markup — there
 * is only ever one source of truth for layout.
 *
 * Industry-specific content (foundry: induction furnaces, DISAMATIC, charge
 * mix, tap-to-tap) is rendered today as labelled placeholders. The intent
 * is to drive these from a per-industry-type generator in a follow-up so
 * cement / textile / chemical / food-processing tenants get the same
 * report shape with the right content.
 *
 * Company name comes from the tenant (`tenantName` prop, resolved via
 * `getCurrentTenantName()` in the parent). Site name + location come from
 * the live `site` record. Personal names (Chairperson, MD, Prepared-by)
 * are placeholders for now — they will be data-bound when we wire a
 * "report contacts" field to the company / site profile.
 */

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { formatReportDate, formatDateTime } from '../../utils/dateUtils';

const API = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Number / currency helpers — match the sample's compact Indian-rupee
// conventions (lakh / crore, "₹1.07 Cr", "₹72.1 lakh") so the report reads
// the same as the reference PDF.
// ---------------------------------------------------------------------------
const fmtINR = (n) => {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1e7) return `₹${(v / 1e7).toFixed(2)} Cr`;
  if (Math.abs(v) >= 1e5) return `₹${(v / 1e5).toFixed(1)} lakh`;
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};
const fmtNum = (n, digits = 0) => {
  if (n == null || isNaN(n)) return '—';
  return Number(n).toLocaleString('en-IN', { maximumFractionDigits: digits, minimumFractionDigits: digits });
};
const pct = (n, digits = 1) => (n == null || isNaN(n) ? '—' : `${Number(n).toFixed(digits)}%`);

// ---------------------------------------------------------------------------
// Palette — matches the sample's tokens. Keep here so future tweaks happen
// in one place and don't drift.
// ---------------------------------------------------------------------------
const NAVY = '#1e3a8a';
const NAVY_DARK = '#0f1e4a';
const YELLOW = '#facc15';
const TEAL_LIGHT = '#67e8f9';
const GREEN = '#059669';

// ---------------------------------------------------------------------------
// Reusable styled primitives. These mirror the sample's repeating patterns —
// extracting them keeps the main component readable and ensures every
// section uses the same spacing, colours, and type weights.
// ---------------------------------------------------------------------------

/**
 * "1. Plant Profile" — green title + thin yellow underline rule.
 *
 * Sizing notes:
 * - `pb-2` adds room beneath descenders so the yellow underline doesn't
 *   clip them when html2canvas rasterises the page.
 * - `mt-12` gives a generous gap above each section header. html2canvas
 *   captures the DOM as one tall image and jsPDF slices it at fixed pixel
 *   intervals, so CSS page-break hints are no-ops; the wide gap means a
 *   heading either sits comfortably at the bottom of a page or starts
 *   cleanly at the top, instead of being sliced through the middle.
 * - `leading-snug` keeps descender extents inside the line box.
 */
const SectionHeader = ({ number, title }) => (
  <div className="mt-12 mb-4">
    <h2 className="text-2xl font-bold text-emerald-600 leading-snug pb-2">
      {number ? `${number}. ` : ''}{title}
    </h2>
    <div className="h-1 bg-yellow-400 mt-3 w-full max-w-[640px]" />
  </div>
);

/**
 * "1.1 Corporate Parameters" — green sub-heading, smaller weight. The
 * generous `mt-8` (was `mt-6`) gives html2canvas's slicer a wider visual
 * gap, reducing the chance a sub-section title gets cut through the
 * middle when the page break lands on it.
 */
const SubHeader = ({ number, title }) => (
  <h3 className="text-lg font-bold text-emerald-600 mt-8 mb-3 pb-1 leading-snug">
    {number ? `${number}  ` : ''}{title}
  </h3>
);

/** Body paragraph — justified, comfortable line-height. */
const P = ({ children, className = '' }) => (
  <p className={`text-gray-800 text-sm leading-relaxed text-justify mb-3 ${className}`}>
    {children}
  </p>
);

/**
 * 5 callout-box variants matching the sample's coloured side rules:
 *   - simulated  → orange  ("SIMULATED DATA")
 *   - insight    → green   ("KEY INSIGHT")
 *   - finding    → green   ("KEY FINDING") — same look, different label
 *   - method     → amber   ("METHOD NOTE")
 *   - opportunity→ blue    ("OPPORTUNITY")
 *
 * Used for inline annotations between body paragraphs and tables.
 */
const Callout = ({ kind = 'insight', label, children }) => {
  const palette = {
    simulated:   { bg: 'bg-orange-50',   bar: 'border-orange-500',   tag: 'text-orange-700',   defaultLabel: 'SIMULATED DATA' },
    insight:     { bg: 'bg-emerald-50',  bar: 'border-emerald-500',  tag: 'text-emerald-700',  defaultLabel: 'KEY INSIGHT' },
    finding:     { bg: 'bg-emerald-50',  bar: 'border-emerald-500',  tag: 'text-emerald-700',  defaultLabel: 'KEY FINDING' },
    method:      { bg: 'bg-amber-50',    bar: 'border-amber-500',    tag: 'text-amber-800',    defaultLabel: 'METHOD NOTE' },
    opportunity: { bg: 'bg-blue-50',     bar: 'border-blue-500',     tag: 'text-blue-700',     defaultLabel: 'OPPORTUNITY' },
  }[kind] || { bg: 'bg-gray-50', bar: 'border-gray-400', tag: 'text-gray-700', defaultLabel: '' };
  return (
    <div className={`${palette.bg} border-l-4 ${palette.bar} rounded-r p-4 my-4`}>
      <p className="text-sm leading-relaxed text-gray-800">
        <span className={`font-bold uppercase tracking-wide text-xs mr-2 ${palette.tag}`}>
          {label || palette.defaultLabel}
        </span>
        {children}
      </p>
    </div>
  );
};

/** Single KPI card — big number + small label + optional caption. */
const KpiCard = ({ value, label, caption, valueColor = 'text-slate-900' }) => (
  <div className="bg-slate-50 rounded-lg px-6 py-6 text-center flex-1">
    <div className={`text-3xl md:text-4xl font-bold ${valueColor}`}>{value}</div>
    <div className="text-emerald-600 text-sm font-semibold mt-2">{label}</div>
    {caption && <div className="text-xs text-gray-500 mt-1">{caption}</div>}
  </div>
);

/** Horizontal row container for KPI cards (2-up or 3-up). */
const KpiRow = ({ children }) => (
  <div className="flex flex-col md:flex-row gap-4 my-4">{children}</div>
);

/**
 * Two-column key/value table used in 1.1 Corporate Parameters and similar
 * sections. Subtle navy header bar, alternating row backgrounds.
 */
const KeyValueTable = ({ rows, leftHeader = 'Parameter', rightHeader = 'Detail' }) => (
  <div className="my-3 rounded overflow-hidden border border-gray-200">
    <table className="w-full text-sm">
      <thead style={{ backgroundColor: NAVY }} className="text-white">
        <tr>
          <th className="text-left px-4 py-3 font-semibold w-1/3">{leftHeader}</th>
          <th className="text-left px-4 py-3 font-semibold">{rightHeader}</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(([k, v], i) => (
          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
            <td className="px-4 py-2.5 text-gray-700">{k}</td>
            <td className="px-4 py-2.5 text-gray-900">{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * Standard data table — navy header bar, alt-row striping. Pass headers as
 * an array of strings; rows as an array of arrays (cells can be JSX so we
 * can colour-code per cell like the sample does for 'Target' / 'Achievable'
 * / 'Deteriorated' badges).
 */
const DataTable = ({ headers, rows, headAlign = [] }) => (
  <div className="my-3 rounded overflow-hidden border border-gray-200">
    <table className="w-full text-sm">
      <thead style={{ backgroundColor: NAVY }} className="text-white">
        <tr>
          {headers.map((h, i) => (
            <th
              key={i}
              className={`px-3 py-3 font-semibold ${headAlign[i] === 'right' ? 'text-right' : 'text-left'}`}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={headers.length} className="px-4 py-3 text-center text-gray-400 italic">
              No data available — populate via on-site measurement campaign.
            </td>
          </tr>
        ) : rows.map((cells, i) => (
          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
            {cells.map((c, j) => (
              <td
                key={j}
                className={`px-3 py-2.5 align-top text-gray-800 ${headAlign[j] === 'right' ? 'text-right' : 'text-left'}`}
              >
                {c}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/**
 * Renders an italic placeholder line — used wherever the sample contains
 * industry-specific narrative we don't yet generate. Calling these out
 * explicitly (rather than inventing fake numbers) keeps the report
 * defensible and auditable: a reader knows which figures are real and
 * which are awaiting industry-specific generators or on-site data.
 */
const Placeholder = ({ children, label = 'PLACEHOLDER' }) => (
  <div className="my-3 px-4 py-3 bg-gray-50 border border-dashed border-gray-300 rounded text-sm text-gray-500 italic">
    <span className="not-italic font-semibold uppercase tracking-wide text-xs text-gray-400 mr-2">
      [{label}]
    </span>
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// AI narrative — replaces the gradient-indigo block in the legacy report.
// Accepts either a JSON-shaped narrative (executive_summary, sections,
// priority_actions, estimated_savings) or plain markdown. Renders sections
// using the shared SubHeader / Callout primitives so the AI output sits
// inside the same visual language as the rest of the report.
// ---------------------------------------------------------------------------

const tryParse = (s) => {
  if (s == null) return null;
  if (typeof s === 'object') return s;
  const t = String(s).trim();
  if (!(t.startsWith('{') || t.startsWith('['))) return null;
  try { return JSON.parse(t); } catch { return null; }
};

const AINarrative = ({ narrative, generatedAt }) => {
  if (!narrative) return null;

  const data = tryParse(narrative);

  return (
    <div className="my-4">
      {generatedAt && (
        <p className="text-xs text-gray-500 mb-2">
          Analysis generated on {formatDateTime(generatedAt)}.
        </p>
      )}

      {/* Structured JSON path — what the LLM emits when prompted with a
          shape. Render each chunk with the same primitives as the rest of
          the report so the styling stays uniform. */}
      {data ? (
        <>
          {data.executive_summary && (
            <Callout kind="insight" label="EXECUTIVE SUMMARY">
              {data.executive_summary}
            </Callout>
          )}
          {data.summary && !data.executive_summary && (
            <P>{data.summary}</P>
          )}

          {Array.isArray(data.sections) && data.sections.map((sec, i) => (
            // mt-8 + leading-paragraph kept-with-next gives the slicer a
            // visible gap before each AI section so the header doesn't
            // land mid-cut on a page boundary.
            <div key={i} className="mt-8">
              {sec.title && <SubHeader title={sec.title} />}
              {sec.content && <P>{sec.content}</P>}
              {Array.isArray(sec.findings) && sec.findings.length > 0 && (
                <>
                  <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mt-2 mb-1">
                    Findings
                  </p>
                  <ul className="list-disc pl-6 mb-3 space-y-1 text-sm text-gray-800 leading-relaxed">
                    {sec.findings.map((f, j) => <li key={j}>{f}</li>)}
                  </ul>
                </>
              )}
              {Array.isArray(sec.recommendations) && sec.recommendations.length > 0 && (
                <>
                  <p className="text-xs font-semibold text-emerald-700 uppercase tracking-wide mt-2 mb-1">
                    Recommendations
                  </p>
                  <ul className="list-disc pl-6 mb-3 space-y-1 text-sm text-gray-800 leading-relaxed">
                    {sec.recommendations.map((r, j) => <li key={j}>{r}</li>)}
                  </ul>
                </>
              )}
              {Array.isArray(sec.subsections) && sec.subsections.map((sub, k) => (
                // py-2 + mt-4 gives the slicer breathing room around each
                // AI sub-block so a heading like "Power Factor Assessment"
                // doesn't get cut mid-line at a page boundary.
                <div key={k} className="ml-2 mt-4 pl-3 py-2 border-l-2 border-emerald-100">
                  {sub.title && (
                    <h4 className="text-sm font-semibold text-gray-800 mb-1.5">{sub.title}</h4>
                  )}
                  {sub.content && (
                    <p className="text-sm text-gray-700 leading-relaxed mb-2">{sub.content}</p>
                  )}
                </div>
              ))}
            </div>
          ))}

          {data.priority_actions && (
            <div className="mt-5">
              <SubHeader title="Priority Actions" />
              {[
                ['immediate',   'Immediate (0–3 months)'],
                ['short_term',  'Short-term (3–12 months)'],
                ['medium_term', 'Medium-term (1–3 years)'],
                ['long_term',   'Long-term (3+ years)'],
              ].map(([key, label]) => {
                const items = data.priority_actions[key];
                if (!Array.isArray(items) || items.length === 0) return null;
                return (
                  <div key={key} className="mb-3">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1">
                      {label}
                    </p>
                    <DataTable
                      headers={['Action', 'Impact', 'Investment']}
                      rows={items.map((it) => [
                        it?.action || '—',
                        it?.impact || '—',
                        it?.investment || '—',
                      ])}
                    />
                  </div>
                );
              })}
            </div>
          )}

          {data.estimated_savings && (
            <div className="mt-5">
              <SubHeader title="Estimated Savings" />
              {data.estimated_savings.summary && <P>{data.estimated_savings.summary}</P>}
              {Array.isArray(data.estimated_savings.items) && data.estimated_savings.items.length > 0 && (
                <DataTable
                  headers={['Area', 'Savings', 'Unit']}
                  headAlign={['left', 'right', 'left']}
                  rows={data.estimated_savings.items.map((it) => [
                    it?.area || '—',
                    typeof it?.savings === 'number' ? it.savings.toLocaleString('en-IN') : (it?.savings ?? '—'),
                    it?.unit || '',
                  ])}
                />
              )}
            </div>
          )}
        </>
      ) : (
        // Plain markdown fallback — react-markdown + tailwind prose handles
        // the heading/bold/list rendering cleanly. No more regex hacks.
        <div className="prose prose-sm max-w-none text-gray-800 prose-headings:text-emerald-700 prose-strong:text-gray-900 prose-p:leading-relaxed prose-p:text-justify prose-li:leading-relaxed">
          <ReactMarkdown>{String(narrative)}</ReactMarkdown>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function OnSiteStudySampleReport({
  study,
  site,
  reportData,
  tenantLogoDataUrl,
  tenantName,
}) {
  const generatedDate = formatReportDate(new Date());
  const studyTitle = study?.study_name || study?.name || 'On-Site Energy Study';
  const siteName = site?.name || '[Site Name]';
  const siteLocation = site?.location || site?.address || '[Site Location]';
  const company = tenantName || '[Company Name]';

  const sessions = reportData?.sessions || [];
  const electrical = reportData?.electrical || [];
  const thermal = reportData?.thermal || [];
  const observations = reportData?.observations || [];
  const insights = reportData?.insights || [];

  // ---------------------------------------------------------------------
  // Derived KPIs from on-site measurement campaign — kept alongside the
  // baseline KPIs we now also surface (electricity MWh, monthly bill,
  // intensity), so reviewers can see both campaign throughput and the
  // baseline anchor in the executive overview.
  // ---------------------------------------------------------------------
  const totalSessions = sessions.length;
  const closedSessions = sessions.filter((s) => s.status === 'Closed').length;
  const totalMeasurements = electrical.length + thermal.length;
  const totalObservations = observations.length;
  const criticalInsights = insights.filter((i) => i.type === 'critical').length;

  // ---------------------------------------------------------------------
  // Live data fetch — desktop baseline, equipment, electricity time-series,
  // SEU identification, opportunity portfolio (Engine V3). Kept inside the
  // component so the parent stays simple and `classic` mode doesn't pay
  // the network cost. Each lookup is wrapped in `.catch` so a partially
  // populated study still renders the rest of the report.
  // ---------------------------------------------------------------------
  const [baseline, setBaseline] = useState(null);              // baseline summary
  const [equipment, setEquipment] = useState([]);              // production equipment list
  const [elecMonthly, setElecMonthly] = useState([]);          // 12-month consumption rows
  const [seuElectric, setSeuElectric] = useState([]);          // SEU identification — electricity
  const [seuFuel, setSeuFuel] = useState([]);                  // SEU identification — fuels
  const [seuSummary, setSeuSummary] = useState(null);          // aggregate SEU summary
  const [portfolio, setPortfolio] = useState(null);            // OE-V3 portfolio
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (!study?.id) { setDataLoading(false); return; }
    const sid = study.id;
    const token = localStorage.getItem('token');
    const cfg = { headers: { Authorization: `Bearer ${token}` } };

    (async () => {
      setDataLoading(true);
      const safeGet = (url) => axios.get(url, cfg).then((r) => r.data).catch(() => null);
      const safePost = (url, body) =>
        axios.post(url, body || {}, cfg).then((r) => r.data).catch(() => null);

      const [
        baselineRes, equipmentRes, elecRes,
        seuElecRes, seuFuelRes, seuSummaryRes,
        portfolioRes,
      ] = await Promise.all([
        safeGet(`${API}/api/ers/data/baseline/${sid}/summary`),
        safeGet(`${API}/api/ers/data/equipment/${sid}`),
        safeGet(`${API}/api/ers/data/electricity/${sid}`),
        safeGet(`${API}/api/ers/seu/${sid}/electricity`),
        safeGet(`${API}/api/ers/seu/${sid}/fuel`),
        safeGet(`${API}/api/ers/seu/summary/${sid}`),
        // Portfolio uses POST + tariff params; we accept platform defaults.
        safePost(`${API}/api/opportunity-v3/matching/study/${sid}/calculate`),
      ]);

      if (cancelled) return;
      setBaseline(baselineRes && baselineRes.calculated ? baselineRes : null);
      setEquipment(Array.isArray(equipmentRes) ? equipmentRes : []);
      setElecMonthly(Array.isArray(elecRes) ? elecRes : []);
      setSeuElectric(Array.isArray(seuElecRes) ? seuElecRes : []);
      setSeuFuel(Array.isArray(seuFuelRes) ? seuFuelRes : []);
      setSeuSummary(seuSummaryRes || null);
      setPortfolio(portfolioRes || null);
      setDataLoading(false);
    })();

    return () => { cancelled = true; };
  }, [study?.id]);

  // ---------------------------------------------------------------------
  // Derived views over the live data. Computed once per render with `||[]`
  // fallbacks so any missing endpoint just collapses to an empty section
  // (which then renders the "No data" row from the DataTable primitive).
  // ---------------------------------------------------------------------

  // Baseline KPI numbers — surface what's actually loaded; fall back to
  // placeholders only when the calculation hasn't been run.
  const elecKwh = baseline?.energy?.electricity_kwh;
  const elecMwh = baseline?.energy?.electricity_mwh;
  const fuelsGj = baseline?.energy?.fuels_gj;
  const totalGj = baseline?.energy?.total_energy_gj;
  const productionMt = baseline?.resources?.production_mt;
  const energyIntensity = baseline?.intensities?.energy_intensity_kwh_per_mt;
  const ghgTotal = baseline?.ghg?.total_tco2e;

  // 12-month profile rows from `ers_electricity_data`. Real columns:
  // month / consumed_units_kwh / md_kva / power_factor / bill_amount /
  // notes. (The previous binding used `period` / `kwh_consumed` /
  // `demand_kva` which don't exist on the table.)
  const monthlyRows = (elecMonthly || [])
    .slice()
    .sort((a, b) => String(a.month || '').localeCompare(String(b.month || '')))
    .slice(0, 24);

  // SEU ranking — concatenate electricity + fuel SEUs, attach % share, sort
  // by annual kWh-equivalent. Fuel rows store energy in GJ; convert to
  // kWh-equivalent for ranking display so a single table makes sense.
  const seuRows = (() => {
    const elec = (seuElectric || []).map((r) => ({
      name: r.equipment_name || r.area || r.seu_id || '—',
      annual_kwh: Number(r.final_annual_energy || r.annual_kwh || 0),
      controllability: r.controllability || r.confidence_level || '—',
      lever: r.primary_savings_lever || r.notes || '—',
      kind: 'electricity',
    }));
    const fuel = (seuFuel || []).map((r) => ({
      name: r.equipment_name || r.area || r.seu_id || '—',
      annual_kwh: Number(r.fuel_energy_gj_yr || 0) * 277.778,
      controllability: r.controllability || r.confidence_level || '—',
      lever: r.primary_savings_lever || r.notes || '—',
      kind: 'fuel',
    }));
    const merged = [...elec, ...fuel].filter((r) => r.annual_kwh > 0);
    const total = merged.reduce((s, r) => s + r.annual_kwh, 0) || 1;
    return merged
      .sort((a, b) => b.annual_kwh - a.annual_kwh)
      .map((r, i) => ({
        ...r,
        rank: i + 1,
        share: (r.annual_kwh / total) * 100,
      }));
  })();

  // Opportunity Engine V3 — split into three phases by PAYBACK PERIOD
  // so the section actually matches its "Prioritised by Payback" title.
  //   Phase 1: payback ≤ 3 months (or zero-capex / Immediate items)
  //   Phase 2: payback 3–12 months
  //   Phase 3: payback > 12 months, OR capex > 0 with no annual-saving
  //            signal yet (no quick payback claim possible — surface so
  //            the reader still sees them).
  // Earlier this file split phases by capex_tier, which produced the
  // visible bug where a Low-CAPEX item with an 18-month payback showed
  // up under the "0–90 Days" heading.
  const capexTier = (avg) => {
    if (avg <= 10000) return 'No CAPEX';
    if (avg <= 500000) return 'Low CAPEX';
    if (avg <= 2500000) return 'Medium CAPEX';
    return 'High CAPEX';
  };
  const computePaybackMonths = (avgCapex, annualINR) => {
    // Returns: 0 for Immediate (zero capex), a positive integer for
    // computable payback, or null when payback can't be inferred (capex
    // > 0 but no annual-saving figure yet). Callers use null to bucket
    // those rows into Phase 3 instead of a phase that silently lies.
    if (avgCapex <= 0) return 0;
    if (annualINR > 0) return Math.round((avgCapex / annualINR) * 12);
    return null;
  };
  const oppCalcsRaw = portfolio?.calculations || [];
  const oppCalcs = oppCalcsRaw.map((c) => {
    const lo = Number(c.estimated_capex_low || 0);
    const hi = Number(c.estimated_capex_high || 0);
    const avg = (lo + hi) / 2;
    const annualINR = Number(c.estimated_annual_savings_inr || 0);
    return {
      ...c,
      avg_capex: avg,
      capex_tier: c.capex_tier || capexTier(avg),
      payback_months: computePaybackMonths(avg, annualINR),
    };
  });
  const phase1 = oppCalcs.filter((c) => c.payback_months !== null && c.payback_months <= 3);
  const phase2 = oppCalcs.filter((c) => c.payback_months !== null && c.payback_months > 3 && c.payback_months <= 12);
  const phase3 = oppCalcs.filter((c) => c.payback_months === null || c.payback_months > 12);
  const sumBy = (arr, fn) => arr.reduce((s, x) => s + (Number(fn(x)) || 0), 0);
  const phase1AnnualSaving = sumBy(phase1, (c) => c.estimated_annual_savings_inr);
  const phase2AnnualSaving = sumBy(phase2, (c) => c.estimated_annual_savings_inr);
  const phase3AnnualSaving = sumBy(phase3, (c) => c.estimated_annual_savings_inr);
  const totalAnnualSaving = phase1AnnualSaving + phase2AnnualSaving + phase3AnnualSaving;
  const phase1Capex = sumBy(phase1, (c) => c.avg_capex || ((c.estimated_capex_low + c.estimated_capex_high) / 2));
  const phase2Capex = sumBy(phase2, (c) => c.avg_capex || ((c.estimated_capex_low + c.estimated_capex_high) / 2));
  const phase3Capex = sumBy(phase3, (c) => c.avg_capex || ((c.estimated_capex_low + c.estimated_capex_high) / 2));
  const totalCapex = phase1Capex + phase2Capex + phase3Capex;
  const totalTco2eAnnual = portfolio?.summary?.total_tco2e_annual ?? sumBy(oppCalcs, (c) => c.tco2e_reduction_annual);

  // Quick 12-month bill estimate (₹) when we have the monthly profile.
  // Falls back to a tariff-based estimate when the bill rows are blank.
  const annualBillINR = monthlyRows.reduce((s, r) => s + Number(r.bill_amount || 0), 0)
    || (Number(baseline?.energy?.electricity_kwh || 0) * 6.5);

  return (
    <div className="bg-white text-gray-900 leading-normal">
      {/* =================================================================
          PAGE 1 — COVER
          ================================================================= */}

      {/* Top header band: navy with study title (white) + Ergoniq tag
          (yellow). The tenant logo used to render here too, but the cover
          had three separate logo placements on a single page — duplicate
          branding that read as visual clutter. The single prominent
          logo on the cover hero banner below is the brand anchor now. */}
      <div style={{ backgroundColor: NAVY }} className="px-6 py-3 rounded-t">
        <p className="text-white text-sm font-semibold leading-tight">
          {company} — {studyTitle}
        </p>
        <p className="text-yellow-300 text-xs">Ergoniq Pre-Assessment</p>
      </div>

      {/* Cover banner: navy block with title + company name + plant strip.
          Anchors a larger version of the company-profile logo at the top
          so the cover reads as belonging to the customer, not just the
          platform. */}
      <div style={{ backgroundColor: NAVY }} className="px-8 py-10 text-white relative">
        {tenantLogoDataUrl && (
          <div className="absolute top-6 right-8">
            <img
              src={tenantLogoDataUrl}
              alt={`${company} logo`}
              className="h-16 max-w-[220px] object-contain bg-white rounded-md p-2"
            />
          </div>
        )}
        <p className="text-yellow-300 text-sm font-bold uppercase tracking-widest mb-3">
          On-Site Energy Audit · v1
        </p>
        <h1 className="text-4xl md:text-5xl font-bold text-yellow-300 leading-tight pr-44">
          {company}
        </h1>
        <p className="text-yellow-200 text-2xl mt-1">{siteName}</p>
        <div className="mt-6 pt-4 border-t border-white/20 text-cyan-200 text-sm">
          {siteLocation} {study?.industry_type && `| ${study.industry_type}`} | Study: {studyTitle}
        </div>
        <p className="mt-1 text-cyan-200 text-sm">
          Ergoniq Deep Energy Intelligence Platform | Saarv Solutions Pvt Ltd
        </p>
      </div>

      {/* IMPORTANT — DECLARATION box (rose, like the sample) */}
      <div className="mx-2 mt-6 bg-rose-50 border border-rose-200 px-5 py-4 rounded">
        <p className="text-rose-700 font-bold uppercase text-sm tracking-wide mb-2">
          Important — Study Declaration
        </p>
        <p className="text-sm text-gray-800 leading-relaxed">
          This is an on-site energy audit prepared by Saarv Solutions Pvt Ltd for {company}.
          Findings combine direct field measurement, plant-floor observation, and benchmark
          comparison. Where measurements have not yet been recorded, sections show explicit
          placeholders. Estimates carry a ±15% uncertainty band unless otherwise stated.
          On-site verification will validate or revise all simulated/benchmark figures.
        </p>
        {dataLoading && (
          <p className="text-xs text-rose-600 mt-2 italic">
            Loading desktop / SEU / opportunity data…
          </p>
        )}
      </div>

      {/* Prepared by + Addressed to (placeholder names). The third
          column used to repeat the tenant logo here, but it was the
          third logo on a single cover page — removed to keep the brand
          anchor on the hero banner only. */}
      <div className="mx-2 mt-6 grid grid-cols-1 md:grid-cols-2 gap-6 text-sm items-start">
        <div>
          <p className="text-gray-500 text-xs uppercase tracking-wide">Prepared by</p>
          <p className="font-bold text-gray-900 mt-1">[Lead Auditor — Name]</p>
          <p className="text-gray-700">[Title], Saarv Solutions Pvt Ltd</p>
          <p className="text-emerald-700">Ergoniq — Deep Energy Intelligence Platform</p>
        </div>
        <div>
          <p className="text-gray-500 text-xs uppercase tracking-wide">Addressed to</p>
          <p className="font-bold text-gray-900 mt-1">[Chairperson Name]</p>
          <p className="font-bold text-gray-900">[Managing Director Name]</p>
          <p className="text-gray-700">{company}</p>
        </div>
      </div>

      {/* Executive Summary — short prose anchored on the real baseline +
          opportunity totals so the page-1 read matches the figures in the
          rest of the report. AI narrative (if generated) is appended as
          additional analysis. */}
      <div className="mx-2 mt-8">
        <h2 className="text-xl font-bold text-emerald-600 mb-2">Executive Summary</h2>
        <P>
          This on-site study identifies a structured energy-saving opportunity at {company}'s
          {siteName} facility.
          {baseline ? (
            <> The analysis is anchored to a calculated baseline of <strong>{fmtNum(elecKwh)} kWh/year</strong> electricity
            ({elecMwh != null ? `${fmtNum(elecMwh, 1)} MWh` : '—'})
            {fuelsGj != null && <> and <strong>{fmtNum(fuelsGj, 1)} GJ/year</strong> fuels</>}
            {productionMt != null && <>, against an annual production anchor of <strong>{fmtNum(productionMt, 0)} MT</strong></>}
            {energyIntensity != null && <> — energy intensity <strong>{fmtNum(energyIntensity, 1)} kWh/MT</strong></>}.
            {ghgTotal != null && <> Resulting GHG footprint: <strong>{fmtNum(ghgTotal, 1)} tCO₂e/year</strong>.</>}
            </>
          ) : (
            <> Run "Calculate Baseline" in the Desktop Study to populate the underlying consumption and intensity figures.</>
          )}
          {oppCalcs.length > 0 && (
            <> The Opportunity Engine V3 has identified <strong>{oppCalcs.length} interventions</strong> totalling
            <strong> {fmtINR(totalAnnualSaving)}/year</strong> of saving against
            <strong> {fmtINR(totalCapex)}</strong> capital outlay
            {totalTco2eAnnual ? <> and <strong>{fmtNum(totalTco2eAnnual, 1)} tCO₂e/year</strong> avoided emissions</> : null}.
            See Section 7 for the prioritised register.</>
          )}
          {' '}All estimates carry a ±15% uncertainty band pending on-site measurement validation.
        </P>
        {reportData?.aiNarrative && (
          <>
            <h3 className="text-base font-bold text-emerald-600 mt-5 mb-2">AI-Generated Analysis</h3>
            <AINarrative narrative={reportData.aiNarrative} generatedAt={reportData.aiGeneratedAt} />
          </>
        )}
      </div>

      {/* =================================================================
          PAGE 2 — KPI ROW
          ================================================================= */}

      <div className="mx-2 mt-10">
        {/* Top row: baseline anchors — same pattern as the sample's
            ~1,000 MT / ₹1.07 Cr cards but driven by this study's
            calculated baseline. Gracefully degrades to '—' when the
            desktop baseline hasn't been calculated yet. */}
        <KpiRow>
          <KpiCard
            value={productionMt != null ? `${fmtNum(productionMt, 0)} MT` : '—'}
            label="Annual Production"
            caption="From desktop baseline"
            valueColor="text-slate-900"
          />
          <KpiCard
            value={annualBillINR ? fmtINR(annualBillINR) : '—'}
            label="Annual Electricity Bill"
            caption={elecKwh ? `${fmtNum(elecKwh)} kWh consumed` : '12-month profile'}
            valueColor="text-slate-900"
          />
          <KpiCard
            value={energyIntensity != null ? `${fmtNum(energyIntensity, 1)}` : '—'}
            label="kWh / MT Output"
            caption="Energy intensity"
            valueColor="text-slate-900"
          />
        </KpiRow>
        {/* Second row: opportunity-engine roll-up. Phase 1 = no/low capex
            from V3. Full programme = phase 1 + phase 2. tCO₂e/year is the
            backend-supplied summary. */}
        <KpiRow>
          <KpiCard
            value={phase1AnnualSaving > 0 ? fmtINR(phase1AnnualSaving) : '—'}
            label="Phase 1 Annual Saving"
            caption={`${phase1.length} opportunity${phase1.length === 1 ? '' : 'ies'} · zero to low capex`}
            valueColor="text-emerald-700"
          />
          <KpiCard
            value={totalAnnualSaving > 0 ? fmtINR(totalAnnualSaving) : '—'}
            label="Full Programme Saving"
            caption={totalCapex > 0 ? `Capex ${fmtINR(totalCapex)}` : 'Phase 1 + Phase 2'}
            valueColor="text-emerald-700"
          />
          <KpiCard
            value={totalTco2eAnnual ? `${fmtNum(totalTco2eAnnual, 1)} tCO₂e` : '—'}
            label="Annual GHG Reduction"
            caption={ghgTotal != null ? `Baseline ${fmtNum(ghgTotal, 0)} tCO₂e` : 'From opportunity portfolio'}
            valueColor="text-emerald-700"
          />
        </KpiRow>
        {/* Smaller second-tier KPIs: on-site campaign throughput so
            reviewers can see at a glance how much measurement underpins
            the numbers above. */}
        <KpiRow>
          <KpiCard
            value={totalSessions}
            label="Measurement Sessions"
            caption={`${closedSessions} completed`}
            valueColor="text-slate-700"
          />
          <KpiCard
            value={totalMeasurements}
            label="Field Measurements"
            caption={`${electrical.length} electrical · ${thermal.length} thermal`}
            valueColor="text-slate-700"
          />
          <KpiCard
            value={totalObservations}
            label="Field Observations"
            caption={`${criticalInsights} critical insight${criticalInsights === 1 ? '' : 's'} flagged`}
            valueColor="text-slate-700"
          />
        </KpiRow>
        {(!baseline || oppCalcs.length === 0) && (
          <Callout kind="method" label="DATA STATUS">
            {!baseline && 'Desktop baseline has not been calculated yet — run "Calculate Baseline" in the Desktop Study to populate the consumption KPIs. '}
            {oppCalcs.length === 0 && 'No opportunities have been calculated yet — open Opportunity Engine V3 → Match & Calculate to populate the savings KPIs.'}
          </Callout>
        )}
      </div>

      {/* =================================================================
          1. PLANT PROFILE
          ================================================================= */}

      <SectionHeader number="1" title={`Plant Profile — ${company}`} />
      <P>
        {company} operates from {siteName}, located at {siteLocation}.
        The corporate parameters below summarise what is currently on file in
        the platform; values marked as placeholders are populated from the
        company / site profile and the on-site measurement campaign.
      </P>

      <SubHeader number="1.1" title="Corporate Parameters" />
      <KeyValueTable
        rows={[
          ['Company', company],
          ['Site', siteName],
          ['Address', siteLocation],
          ['Industry Type', study?.industry_type || '[Industry Type]'],
          ['Established', '[Year]'],
          ['Chairperson', '[Chairperson Name]'],
          ['Managing Director', '[Managing Director Name]'],
          ['Production Units', '[Number of Units]'],
          ['Installed Capacity', '[Capacity / Month]'],
          ['Study Anchor Production', '[Anchor Production / Month]'],
          ['Certifications', '[ISO / IATF / Other]'],
          ['Key Markets', '[Markets Served]'],
          ['Key Clients', '[Declared Clients]'],
        ]}
      />

      <SubHeader number="1.2" title="Production Equipment Inventory" />
      <Callout kind="simulated">
        Equipment inventory below is sourced from the desktop study equipment
        register. Rated parameters reflect manufacturer specifications; actual
        connected loads and load factors are confirmed via the on-site
        measurement campaign in Section 8.
      </Callout>
      {/* Column shape mirrors `ers_equipment` (see backend tenant_ddl.py):
          equipment_type / equipment_id / rated_power_kw / qty / notes.
          The previous bindings used invented field names (equipment_name,
          make, model, rated_capacity) that don't exist on the table, so
          the first three columns rendered "—" for every row. */}
      <DataTable
        headers={['Equipment', 'ID / Location', 'Rated Power', 'Energy Relevance']}
        headAlign={['left', 'left', 'right', 'left']}
        rows={(equipment || []).map((e) => [
          e.equipment_type || '—',
          [e.equipment_id, e.location].filter(Boolean).join(' · ') || '—',
          e.rated_power_kw != null
            ? `${fmtNum(e.rated_power_kw, 1)} kW${e.qty > 1 ? ` × ${e.qty}` : ''}`
            : '—',
          e.notes || '—',
        ])}
      />

      <SubHeader number="1.3" title="Capacity Cross-Check" />
      <Placeholder label="INDUSTRY-SPECIFIC">
        Capacity cross-check reconciles declared installed capacity against
        equipment-level throughput to confirm the production anchor is
        physically achievable. The reconciliation logic depends on industry
        type and is rendered by the per-industry generator.
      </Placeholder>

      {/* =================================================================
          2. ENERGY CONSUMPTION ANALYSIS
          ================================================================= */}

      <SectionHeader number="2" title="Energy Consumption Analysis — Bill Reconciliation and Baseline" />
      <P>
        This section establishes the energy consumption baseline through a
        structured reconciliation of the declared electricity bill against
        plant production parameters. Where calculations require assumptions
        that carry material uncertainty, a sensitivity note is provided.
      </P>

      <SubHeader number="2.1" title="Tariff Structure" />
      <Placeholder label="INDUSTRY-SPECIFIC / TARIFF">
        Tariff breakdown (energy charge, demand charge, electricity duty,
        surcharges) is sourced from the utility tariff schedule applicable
        to the site. Will populate from the tariff model linked to the site.
      </Placeholder>

      <SubHeader number="2.2" title="Bill Reconciliation — Calculation Chain" />
      <DataTable
        headers={['Step', 'Parameter', 'Calculation', 'Result', 'Uncertainty']}
        rows={baseline ? [
          ['1', 'Total annual electricity', '∑ monthly bills', `${fmtNum(elecKwh)} kWh`, 'Low — declared bill'],
          ['2', 'Total fuels (thermal)', '∑ fuel records → GJ', `${fmtNum(fuelsGj, 1)} GJ`, 'Low — declared bill'],
          ['3', 'Total energy', 'Electricity + fuels (GJ basis)', `${fmtNum(totalGj, 1)} GJ`, '±5%'],
          ['4', 'Production', 'From production records', `${fmtNum(productionMt, 0)} MT`, '±5%'],
          ['5', 'Energy intensity', 'Total kWh ÷ Production MT', `${fmtNum(energyIntensity, 1)} kWh/MT`, '±10%'],
          ['6', 'Scope 1 GHG', '∑ fuels × emission factor', `${fmtNum(baseline?.ghg?.scope1_tco2e, 1)} tCO₂e`, '±10%'],
          ['7', 'Scope 2 GHG', 'Electricity × grid EF', `${fmtNum(baseline?.ghg?.scope2_tco2e, 1)} tCO₂e`, '±10%'],
          ['8', 'Total GHG', 'Scope 1 + Scope 2', `${fmtNum(ghgTotal, 1)} tCO₂e`, '±10%'],
        ] : []}
      />

      <SubHeader number="2.3" title="Sensitivity Analysis" />
      <Placeholder label="INDUSTRY-SPECIFIC">
        Sensitivity analysis surfaces how the SEC and energy share assumptions
        drive the production estimate, and which assumptions deserve direct
        measurement. Runs once industry-specific benchmarks are wired.
      </Placeholder>

      <SubHeader number="2.4" title="Consolidated Energy Baseline" />
      <KpiRow>
        <KpiCard
          value={elecMwh != null ? `${fmtNum(elecMwh, 1)}` : '—'}
          label="MWh / Year — Electricity"
          caption={elecKwh ? `${fmtNum(elecKwh)} kWh` : '±5% uncertainty band'}
        />
        <KpiCard
          value={fuelsGj != null ? `${fmtNum(fuelsGj, 1)}` : '—'}
          label="GJ / Year — Fuels"
          caption={totalGj != null ? `Total ${fmtNum(totalGj, 1)} GJ` : 'Scope 1 thermal'}
        />
        <KpiCard
          value={energyIntensity != null ? `${fmtNum(energyIntensity, 1)}` : '—'}
          label="kWh / MT Output"
          caption="Total plant energy intensity"
        />
      </KpiRow>
      <KpiRow>
        <KpiCard
          value={ghgTotal != null ? `${fmtNum(ghgTotal, 1)}` : '—'}
          label="tCO₂e / Year — Total"
          caption={`Scope 1 ${pct(baseline?.ghg?.scope1_percentage)} · Scope 2 ${pct(baseline?.ghg?.scope2_percentage)}`}
        />
        <KpiCard
          value={baseline?.energy?.renewable_percentage != null ? pct(baseline?.energy?.renewable_percentage) : '—'}
          label="Renewable Share"
          caption={baseline?.energy?.renewable_kwh ? `${fmtNum(baseline.energy.renewable_kwh)} kWh` : 'Of electricity input'}
        />
      </KpiRow>

      <SubHeader number="2.5" title="12-Month Consumption Profile" />
      <DataTable
        headers={['Month', 'kWh Consumed', 'Bill (₹)', 'MD (kVA)', 'PF', 'Observation']}
        headAlign={['left', 'right', 'right', 'right', 'right', 'left']}
        rows={monthlyRows.map((r) => {
          const kwh = Number(r.consumed_units_kwh || 0);
          const bill = Number(r.bill_amount || 0);
          const effective = kwh > 0 && bill > 0 ? `${(bill / kwh).toFixed(2)} ₹/kWh effective` : '—';
          return [
            r.month || '—',
            fmtNum(kwh),
            fmtNum(bill, 0),
            r.md_kva != null ? fmtNum(r.md_kva, 1) : '—',
            r.power_factor != null ? Number(r.power_factor).toFixed(2) : '—',
            r.notes || effective,
          ];
        })}
      />

      {/* =================================================================
          3. SEU ANALYSIS
          ================================================================= */}

      <SectionHeader number="3" title="Significant Energy User (SEU) Analysis" />
      <P>
        SEU identification determines where audit effort, measurement, and
        capital should be concentrated. Ranking uses two axes: estimated
        monthly consumption and controllability — the ease with which
        consumption can be reduced without impacting production.
      </P>

      <SubHeader number="3.1" title="SEU Ranking Table" />
      {seuSummary && (
        <P>
          {seuSummary.total_seus || seuRows.length} significant energy users identified
          across electricity and thermal categories.
          {seuSummary.total_kwh_yr && ` Combined consumption: ${fmtNum(seuSummary.total_kwh_yr)} kWh/yr.`}
        </P>
      )}
      <DataTable
        headers={['Rank', 'SEU', 'Est. kWh/Year', '% of Total', '₹/Year (@ ₹6.50)', 'Confidence', 'Primary Lever']}
        headAlign={['left', 'left', 'right', 'right', 'right', 'left', 'left']}
        rows={seuRows.map((r) => {
          const annualINR = r.annual_kwh * 6.5;
          return [
            r.rank,
            <span className="font-semibold text-gray-900">{r.name}</span>,
            fmtNum(r.annual_kwh),
            pct(r.share, 1),
            fmtINR(annualINR),
            <span className={`px-2 py-0.5 rounded text-xs font-medium ${
              String(r.controllability).toLowerCase() === 'high' ? 'bg-emerald-100 text-emerald-700' :
              String(r.controllability).toLowerCase() === 'medium' ? 'bg-amber-100 text-amber-700' :
              String(r.controllability).toLowerCase() === 'low' ? 'bg-rose-100 text-rose-700' :
              'bg-gray-100 text-gray-600'
            }`}>{r.controllability || '—'}</span>,
            r.lever,
          ];
        })}
      />

      <SubHeader number="3.2" title="Yield Factor — An Often-Missed Lever" />
      <Placeholder label="INDUSTRY-SPECIFIC">
        Yield (good output ÷ total input) directly affects effective SEC and
        the bill. Industry-specific yield benchmarks and improvement
        opportunities will populate this section.
      </Placeholder>

      {/* =================================================================
          4. PRIMARY SEU DEEP DIVE (industry-specific)
          ================================================================= */}

      <SectionHeader number="4" title="Primary SEU Deep Dive" />
      <Placeholder label="INDUSTRY-SPECIFIC">
        For foundry tenants this section is "Furnace Energy Deep Dive"
        (theoretical minimum, energy waterfall, tap-to-tap, charge mix).
        For other industries the structure is the same — theoretical minimum
        of the dominant load, loss waterfall, operational KPI, input quality
        impact — but the parameters and benchmarks differ. Driven by the
        per-industry generator.
      </Placeholder>

      {/* =================================================================
          5. UTILITIES ASSESSMENT
          ================================================================= */}

      <SectionHeader number="5" title="Utilities Assessment — Compressed Air, Cooling, Lighting" />
      <P>
        Utility systems collectively consume a significant share of total
        plant energy. Unlike the dominant SEU, virtually all utility losses
        are operational and immediately controllable. The savings rate per
        rupee of audit effort is highest in utilities.
      </P>

      <SubHeader number="5.1" title="Compressed Air System" />
      <Placeholder label="INDUSTRY-AGNOSTIC">
        Compressor-level loss table (leaks, overpressure, idle running,
        demand spikes) populated from on-site ultrasonic survey + pressure
        log measurements. Section 8 below describes the measurement plan
        that fills this in.
      </Placeholder>

      <SubHeader number="5.2" title="Cooling Systems" />
      <DataTable
        headers={['Sub-System', 'kW', 'hrs/Day', 'kWh/Month', 'Key Waste', 'Intervention']}
        rows={[]}
      />

      <SubHeader number="5.3" title="Hidden SEUs" />
      <Placeholder label="INDUSTRY-SPECIFIC">
        Loads that are typically un-metered in this industry — e.g. machining
        spindle + coolant + chip conveyor for foundries; humidification +
        compressed air for textiles. Ergoniq sub-metering surfaces these.
      </Placeholder>

      {/* =================================================================
          6. OPERATIONAL & BEHAVIOURAL LOSS
          ================================================================= */}

      <SectionHeader number="6" title="Operational and Behavioural Loss Assessment" />
      <P>
        Operational losses are the most underestimated category in any
        industrial energy audit — and the most immediately recoverable. They
        do not correspond to equipment age or technology gaps. They are the
        accumulated cost of practices that have never been measured and
        therefore never challenged. The missing ingredient is energy
        visibility.
      </P>
      <Callout kind="simulated">
        Loss figures populate from on-site shift observations + sub-metering.
        Until the on-site campaign runs, this table summarises measurement
        targets rather than verified losses.
      </Callout>
      <DataTable
        headers={['Operational Loss', 'Mechanism', 'kWh/Month', '₹/Month', 'Fix', 'Capex']}
        rows={[]}
      />

      {/* =================================================================
          7. OPPORTUNITY REGISTER
          ================================================================= */}

      <SectionHeader number="7" title="Opportunity Register — Prioritised by Payback" />
      <P>
        All identified opportunities are consolidated here in a single
        prioritised register, organised into two tiers — Phase 1 (zero to
        minimal capital, deliverable in 90 days) and Phase 2 (capital
        programme, 3–12 months).
      </P>
      <Callout kind="method">
        Financial conversions: kWh savings × tariff energy rate = energy
        charge saving. Demand charge savings (PF correction, peak shaving)
        are calculated separately on avoided kVA demand and added to the
        relevant line items. All opportunity values are annual.
      </Callout>

      {/* Helper closures local to this section so the Phase 1 / 2 / 3
          rows render identically without repeating the formatting block.
          Each row maps an Opportunity Engine V3 calculation to the
          sample's column shape: # / intervention / kWh-equiv saved / ₹
          annual saving / capex range / payback. The payback label uses
          the pre-computed payback_months on each calc so the value the
          row shows is the same value the phase filter used to bucket it. */}
      {(() => {
        const renderOppRows = (calcs, prefix) =>
          calcs.map((c, i) => {
            const energyValue = c.energy_savings_value || c.energy_savings_kwh || 0;
            const monthlyEnergy = energyValue / 12;
            const annualINR = c.estimated_annual_savings_inr || 0;
            const capexLow = c.estimated_capex_low || 0;
            const capexHigh = c.estimated_capex_high || 0;
            const m = c.payback_months;
            // Mirror the payback_months value used for filtering. null
            // means "no annual-saving signal yet" — show em-dash.
            let paybackLabel = '—';
            if (m === 0) paybackLabel = 'Immediate';
            else if (m !== null && m !== undefined) {
              paybackLabel = m <= 1 ? '< 1 month' : m > 24 ? '> 24 months' : `${m} months`;
            }
            return [
              `${prefix}.${i + 1}`,
              <div>
                <span className="font-semibold text-gray-900">{c.opportunity_name || c.capex_item_name || '—'}</span>
                {c.seu_name && <div className="text-xs text-gray-500 mt-0.5">SEU: {c.seu_name}</div>}
              </div>,
              `${fmtNum(monthlyEnergy)} ${c.energy_savings_unit || 'kWh'}`,
              <span className="font-semibold text-emerald-700">{fmtINR(annualINR)}</span>,
              capexLow === 0 && capexHigh === 0 ? 'Zero' : `${fmtINR(capexLow)}–${fmtINR(capexHigh)}`,
              <span className={`text-xs font-medium ${
                paybackLabel === 'Immediate' || paybackLabel === '< 1 month' ? 'text-emerald-700' :
                paybackLabel.startsWith('>') || paybackLabel === '—' ? 'text-rose-700' : 'text-amber-700'
              }`}>{paybackLabel}</span>,
            ];
          });
        return (
          <>
            <SubHeader number="7.1" title="Phase 1 — Operational and Zero-Capital (0–90 Days)" />
            <DataTable
              headers={['#', 'Intervention', 'kWh Saved/Month', 'Annual Saving', 'Capex', 'Payback']}
              headAlign={['left', 'left', 'right', 'right', 'left', 'left']}
              rows={renderOppRows(phase1, 1)}
            />
            {phase1.length > 0 && (
              <Callout kind="finding" label="PHASE 1 SUMMARY">
                Phase 1 delivers <strong>{fmtINR(phase1AnnualSaving)}/year</strong> from
                {' '}{phase1.length} operational intervention{phase1.length === 1 ? '' : 's'} at
                {' '}{phase1Capex > 0 ? fmtINR(phase1Capex) + ' total capex' : 'zero capex'}. These wins
                fund the capital list in Section 7.2.
              </Callout>
            )}

            <SubHeader number="7.2" title="Phase 2 — Capital Programme (Months 3–12)" />
            <DataTable
              headers={['#', 'Intervention', 'kWh Saved/Month', 'Annual Saving', 'Capex', 'Payback']}
              headAlign={['left', 'left', 'right', 'right', 'left', 'left']}
              rows={renderOppRows(phase2, 2)}
            />

            <SubHeader number="7.3" title="Phase 3 — Long Payback (12+ Months)" />
            <DataTable
              headers={['#', 'Intervention', 'kWh Saved/Month', 'Annual Saving', 'Capex', 'Payback']}
              headAlign={['left', 'left', 'right', 'right', 'left', 'left']}
              rows={renderOppRows(phase3, 3)}
            />
            {phase3.length > 0 && (
              <Callout kind="method" label="PHASE 3 NOTE">
                These opportunities have a payback beyond 12 months — or
                no quantified annual saving yet — and are listed here for
                completeness. They typically depend on on-site verification
                or strategic capex decisions before scheduling.
              </Callout>
            )}
          </>
        );
      })()}

      <SubHeader number="7.4" title="Consolidated Opportunity Summary" />
      <Callout kind="method">
        This table is the single source of truth for all financial claims
        in this report. The Executive Summary KPIs derive from this table.
        ±15% uncertainty band applies to all rows.
      </Callout>
      <DataTable
        headers={['Category', 'Annual Saving', '±15% Range', 'Capex', 'Payback (blended)']}
        headAlign={['left', 'right', 'left', 'right', 'left']}
        rows={oppCalcs.length === 0 ? [] : [
          [
            <span className="font-semibold text-emerald-700">Phase 1 — 0–3 mo payback ({phase1.length})</span>,
            <span className="font-semibold text-emerald-700">{fmtINR(phase1AnnualSaving)}</span>,
            `${fmtINR(phase1AnnualSaving * 0.85)} – ${fmtINR(phase1AnnualSaving * 1.15)}`,
            phase1Capex > 0 ? fmtINR(phase1Capex) : 'Zero',
            phase1AnnualSaving > 0 ? `${Math.max(1, Math.round(phase1Capex / phase1AnnualSaving * 12))} months` : '—',
          ],
          [
            <span className="font-semibold text-emerald-700">Phase 2 — 3–12 mo payback ({phase2.length})</span>,
            <span className="font-semibold text-emerald-700">{fmtINR(phase2AnnualSaving)}</span>,
            `${fmtINR(phase2AnnualSaving * 0.85)} – ${fmtINR(phase2AnnualSaving * 1.15)}`,
            phase2Capex > 0 ? fmtINR(phase2Capex) : 'Zero',
            phase2AnnualSaving > 0 ? `${Math.round(phase2Capex / phase2AnnualSaving * 12)} months` : '—',
          ],
          [
            <span className="font-semibold text-emerald-700">Phase 3 — 12+ mo payback ({phase3.length})</span>,
            <span className="font-semibold text-emerald-700">{fmtINR(phase3AnnualSaving)}</span>,
            `${fmtINR(phase3AnnualSaving * 0.85)} – ${fmtINR(phase3AnnualSaving * 1.15)}`,
            phase3Capex > 0 ? fmtINR(phase3Capex) : 'Zero',
            phase3AnnualSaving > 0 ? `${Math.round(phase3Capex / phase3AnnualSaving * 12)} months` : '—',
          ],
          [
            <span className="font-bold text-gray-900">FULL PROGRAMME TOTAL</span>,
            <span className="font-bold text-emerald-700">{fmtINR(totalAnnualSaving)}</span>,
            `${fmtINR(totalAnnualSaving * 0.85)} – ${fmtINR(totalAnnualSaving * 1.15)}`,
            <span className="font-semibold">{fmtINR(totalCapex)}</span>,
            totalAnnualSaving > 0 ? `${Math.max(1, Math.round(totalCapex / totalAnnualSaving * 12))} months blended` : '—',
          ],
          ...(totalTco2eAnnual ? [[
            <span className="font-semibold text-gray-700">GHG reduction</span>,
            `${fmtNum(totalTco2eAnnual, 1)} tCO₂e/yr`,
            '—',
            '—',
            'Annual',
          ]] : []),
        ]}
      />

      {/* =================================================================
          8. ON-SITE MEASUREMENT PLAN — uses real session/measurement data
          ================================================================= */}

      <SectionHeader number="8" title="On-Site Measurement Plan — Converting Simulation to Verified Data" />
      <P>
        The measurement campaign replaces every simulated figure in this
        report with an instrument-verified value. It is the bridge between
        this on-site assessment and the Ergoniq platform baseline.
      </P>

      {sessions.length > 0 ? (
        <>
          <SubHeader title="Measurement Sessions Recorded" />
          <DataTable
            headers={['Session', 'Date', 'Shift', 'Condition', 'Measured By', 'Status']}
            rows={sessions.map((s) => [
              s.session_id || '—',
              s.date || '—',
              s.shift || '—',
              s.condition || '—',
              s.measured_by || '—',
              s.status === 'Closed' ? (
                <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 text-xs font-medium">
                  Completed
                </span>
              ) : (
                <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-700 text-xs font-medium">
                  In Progress
                </span>
              ),
            ])}
          />
        </>
      ) : (
        <Placeholder label="MEASUREMENT CAMPAIGN">
          No measurement sessions have been recorded yet. Start the on-site
          campaign from the On-Site module to populate this section.
        </Placeholder>
      )}

      {electrical.length > 0 && (
        <>
          <SubHeader number="8.1" title="Electrical Measurements" />
          <DataTable
            headers={['Session', 'SEU', 'Equipment', 'V', 'A', 'kW', 'PF', 'Desktop kWh/yr', 'Measured kWh/yr', 'Variance']}
            headAlign={['left','left','left','right','right','right','right','right','right','right']}
            rows={electrical.map((m) => {
              const variance = m.desktop_kwh && m.measured_kwh
                ? ((m.measured_kwh - m.desktop_kwh) / m.desktop_kwh * 100).toFixed(1)
                : null;
              const varColor = variance == null
                ? 'text-gray-400'
                : (parseFloat(variance) < 0 ? 'text-emerald-700' : 'text-rose-700');
              return [
                m.session_id || '—',
                <span className="font-medium text-amber-700">{m.seu_code || '—'}</span>,
                m.equipment || '—',
                m.voltage?.toFixed(1) ?? '—',
                m.current?.toFixed(1) ?? '—',
                m.power_kw?.toFixed(1) ?? '—',
                m.power_factor?.toFixed(2) ?? '—',
                m.desktop_kwh?.toLocaleString() ?? '—',
                m.measured_kwh?.toLocaleString() ?? '—',
                <span className={`font-medium ${varColor}`}>{variance != null ? `${variance}%` : '—'}</span>,
              ];
            })}
          />
        </>
      )}

      {thermal.length > 0 && (
        <>
          <SubHeader number="8.2" title="Thermal / Combustion Measurements" />
          <DataTable
            headers={['Session', 'SEU', 'Equipment', 'Stack Temp (°C)', 'O₂ (%)', 'CO (ppm)', 'Efficiency (%)']}
            headAlign={['left','left','left','right','right','right','right']}
            rows={thermal.map((m) => [
              m.session_id || '—',
              <span className="font-medium text-orange-700">{m.seu_code || '—'}</span>,
              m.equipment || '—',
              m.stack_temp ?? '—',
              m.o2_percent ?? '—',
              m.co_ppm ?? '—',
              m.efficiency ?? '—',
            ])}
          />
        </>
      )}

      {observations.length > 0 && (
        <>
          <SubHeader number="8.3" title="Field Observations" />
          <DataTable
            headers={['Session', 'SEU', 'Type', 'Description', 'Severity', 'Impact']}
            rows={observations.map((o) => [
              o.session_id || '—',
              <span className="font-medium text-purple-700">{o.seu_code || '—'}</span>,
              o.type || '—',
              o.description || '—',
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                o.severity === 'High' ? 'bg-rose-100 text-rose-700' :
                o.severity === 'Medium' ? 'bg-amber-100 text-amber-700' :
                'bg-emerald-100 text-emerald-700'
              }`}>{o.severity || '—'}</span>,
              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                o.impact === 'High' ? 'bg-rose-100 text-rose-700' :
                o.impact === 'Medium' ? 'bg-amber-100 text-amber-700' :
                'bg-emerald-100 text-emerald-700'
              }`}>{o.impact || '—'}</span>,
            ])}
          />
        </>
      )}

      {insights.length > 0 && (
        <>
          <SubHeader number="8.4" title="Auto-Detected Insights" />
          <div className="space-y-2 my-3">
            {insights.map((it, idx) => {
              const colorMap = {
                critical: { bg: 'bg-rose-50',    bar: 'border-rose-500',    tag: 'bg-rose-100 text-rose-700' },
                warning:  { bg: 'bg-amber-50',   bar: 'border-amber-500',   tag: 'bg-amber-100 text-amber-700' },
                opportunity: { bg: 'bg-emerald-50', bar: 'border-emerald-500', tag: 'bg-emerald-100 text-emerald-700' },
              };
              const c = colorMap[it.type] || { bg: 'bg-gray-50', bar: 'border-gray-300', tag: 'bg-gray-100 text-gray-700' };
              return (
                <div key={idx} className={`${c.bg} border-l-4 ${c.bar} rounded p-3`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.tag}`}>{it.category}</span>
                    <span className="text-xs text-gray-500">{it.seu_code}</span>
                  </div>
                  <p className="text-sm font-medium text-gray-900">{it.trigger}</p>
                  <p className="text-xs text-gray-700 mt-0.5">{it.recommendation}</p>
                  {it.potential_savings && (
                    <p className="text-xs text-gray-500 mt-1">Potential: {it.potential_savings}</p>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <SubHeader number="8.5" title="What Each Measurement Replaces" />
      <DataTable
        headers={['Simulated Parameter', 'Instrument Measurement', 'Confidence Gain']}
        rows={[
          ['Specific Energy Consumption (benchmark)', 'Power analyser kWh per cycle ÷ output from production log = actual SEC', 'Eliminates primary assumption — direct measurement'],
          ['Equipment share of total (benchmark)', 'Sub-metered kWh ÷ main meter kWh = actual share', 'Eliminates second-largest assumption'],
          ['Compressor leak losses (industry norm)', 'Ultrasonic survey: each leak located, flow estimated. Sum ÷ rated output = actual leak %', 'Direct measurement — highly accurate'],
          ['Operating hours per SEU (production logic)', 'Data logger on each feeder: actual hours at load, partial load, idle', 'Eliminates estimated operating hours across all SEUs'],
          ['Power factor (typical for industry)', 'Power analyser records PF continuously per shift', 'Confirms or revises PF penalty — immediate financial impact'],
          ['Total kWh/month (bill calculation)', 'Multi-day data-logger accumulation × scaling factor vs actual meter', 'Cross-validates entire energy balance'],
        ]}
      />

      {/* =================================================================
          9. ERGONIQ PLATFORM
          ================================================================= */}

      <SectionHeader number="9" title="The Ergoniq Platform — From Pre-Assessment to Continuous Intelligence" />
      <P>
        The on-site study in this document is the pre-assessment phase. The
        measurement campaign validates it. What follows is the Ergoniq
        platform — the continuous intelligence layer that converts a
        one-time audit into a permanent energy management capability.
      </P>

      <SubHeader number="9.1" title="Four Platform Layers" />
      <DataTable
        headers={['Layer', 'Function', `At ${siteName} — Specifically`]}
        rows={[
          ['Layer 1 · Data Ingestion', 'Continuous data collection from all measurement points', 'Smart sub-meters on dominant SEU feeders + utility incomers. Data to Ergoniq cloud at 1-minute intervals. Bill ingested monthly. Production logs entered via tablet by shift supervisor.'],
          ['Layer 2 · Structuring', 'Tag every kWh to context — process, shift, product, operator', 'Each kWh tagged: SEU, shift, product/batch, operator team. Per-shift and per-batch energy intensity calculated automatically. Baseline established from first 30 days of live data.'],
          ['Layer 3 · Opportunity Engine', 'Systematic identification and quantification of efficiency gaps', 'Algorithms compare actual energy intensity against baseline. Shift-level spike >5% raises an alert with probable cause (idle, charge issue, compressor anomaly). Each Phase 1 intervention tracked: actual vs projected saving, live.'],
          ['Layer 4 · Reporting', 'Daily, weekly, monthly accountability loop', 'Daily dashboard: plant manager + shift supervisors. Weekly intensity report: automatic to MD. Monthly performance report: savings realised vs projected, variance analysis, updated opportunity register.'],
        ]}
      />

      {/* =================================================================
          10. CONCLUSION + PLAN OF ACTION
          ================================================================= */}

      <SectionHeader number="10" title="Conclusion and Plan of Action" />
      <P>
        This on-site study has established a measured baseline for {company}.
        The key findings below summarise what was observed and quantified
        during the measurement campaign, followed by a concrete plan of
        action with named milestones, expected results, and accountabilities.
      </P>

      <SubHeader number="10.1" title="Key Findings — Summary" />
      <div style={{ backgroundColor: NAVY }} className="rounded p-5 my-4 text-white">
        <p className="text-yellow-300 font-bold text-center text-lg mb-3">
          Five Facts About This Plant's Energy
        </p>
        <ol className="list-decimal pl-6 space-y-2 text-sm leading-relaxed">
          <li>
            {baseline ? (
              <>The plant consumes <strong>{fmtNum(elecKwh)} kWh/year</strong>{fuelsGj != null && <> plus <strong>{fmtNum(fuelsGj, 1)} GJ/year</strong> in fuels</>}
              {productionMt != null && <>, internally consistent with <strong>{fmtNum(productionMt, 0)} MT/year</strong> of production</>}
              {energyIntensity != null && <> at <strong>{fmtNum(energyIntensity, 1)} kWh/MT</strong></>}.</>
            ) : (
              <>Baseline consumption pending — run "Calculate Baseline" in Desktop Study.</>
            )}
          </li>
          <li>
            {seuRows.length > 0 ? (
              <>{seuRows.length} significant energy users identified. Top SEU: <strong>{seuRows[0]?.name}</strong> at {fmtNum(seuRows[0]?.annual_kwh)} kWh/year ({pct(seuRows[0]?.share)} of total).</>
            ) : (
              <>SEU identification pending in the Desktop Study.</>
            )}
          </li>
          <li>
            {phase1.length > 0 ? (
              <>Phase 1 operational opportunities: <strong>{phase1.length}</strong> intervention{phase1.length === 1 ? '' : 's'} = <strong>{fmtINR(phase1AnnualSaving)}/year</strong> at {phase1Capex > 0 ? fmtINR(phase1Capex) : 'zero'} capex. Recoverable in 0–90 days.</>
            ) : (
              <>Phase 1 opportunities pending — run Opportunity Engine V3 → Match & Calculate.</>
            )}
          </li>
          <li>
            {phase2.length > 0 ? (
              <>Phase 2 capital programme: <strong>{phase2.length}</strong> intervention{phase2.length === 1 ? '' : 's'} = <strong>{fmtINR(phase2AnnualSaving)}/year</strong> at {fmtINR(phase2Capex)} capex.</>
            ) : (
              <>Phase 2 capital programme pending opportunity calculation.</>
            )}
          </li>
          <li>
            {oppCalcs.length > 0 ? (
              <>Total identified saving: <strong>{fmtINR(totalAnnualSaving)}/year</strong> against <strong>{fmtINR(totalCapex)}</strong> capex.
              {totalTco2eAnnual ? <> {fmtNum(totalTco2eAnnual, 1)} tCO₂e/year emissions avoided.</> : null}
              {totalAnnualSaving > 0 && <> Blended payback: <strong>{Math.max(1, Math.round(totalCapex / totalAnnualSaving * 12))} months</strong>.</>}</>
            ) : (
              <>Total saving + capex roll-up will populate once the opportunity engine has run.</>
            )}
          </li>
        </ol>
        <p className="text-cyan-200 text-xs mt-4 pt-3 border-t border-white/20">
          Captured during {totalSessions} measurement session{totalSessions === 1 ? '' : 's'} ({closedSessions} closed) ·
          {' '}{electrical.length + thermal.length} instrumented data points · {totalObservations} field observation{totalObservations === 1 ? '' : 's'}
          {criticalInsights > 0 && ` · ${criticalInsights} critical`}.
        </p>
      </div>

      <SubHeader number="10.2" title="Plan of Action — Milestones, Results, Accountabilities" />
      <DataTable
        headers={['Milestone', 'Timeline', 'Action', 'Expected Result', 'Accountability']}
        rows={[
          ['M0 — Engagement Confirmation', 'Week 1', 'NDA signed. Engagement scope agreed. 12 months of bill data shared. 6 months of production records shared.', 'Scope of measurement campaign finalised. Simulated figures pre-validated against actual bill data.', `${company} MD + Saarv`],
          ['M1 — On-Site Measurement Campaign', 'Weeks 2–3', 'Multi-day measurement campaign per Section 8. Power analysers, ultrasonic survey, thermal imaging, operator interviews.', 'All simulated parameters replaced with measured values. Revised opportunity register with verified kWh and ₹ figures.', 'Saarv engineering team'],
          ['M2 — Validated Study Published', 'Week 4', 'This document revised: all simulated figures replaced. Opportunity register confirmed. Phase 1 action list finalised.', 'Business case ready for MD approval of Phase 1 + sub-metering investment.', 'Saarv'],
          ['M3 — Sub-Metering Live', 'Month 2', 'Sub-meters installed on dominant SEU feeders + utilities. Ergoniq platform configured. Data ingestion live. 30-day baseline measurement begins.', 'First shift-level energy dashboard visible to plant manager. Baseline established for all saving claims.', `Saarv + ${company} plant team`],
          ['M4 — Phase 1 Operational Wins', 'Month 2', 'Implement Phase 1 zero-capex items: shift protocol, batch planning, charge mix discipline, leak sealing, pressure reduction, scheduling policies.', 'Phase 1 saving rate visible on Ergoniq dashboard within 30 days of protocol implementation.', `${company} operations + shift supervisors`],
          ['M5 — 30-Day Review', 'Month 3', 'Joint review of first 30-day platform data. Compare actual vs projected Phase 1 savings. Approve Phase 2 capital list.', 'Phase 1 savings confirmed on platform. Phase 2 investment decision made on verified data, not benchmarks.', `${company} MD + Saarv`],
          ['M6 — Phase 2 Deployment', 'Months 3–5', 'Capital programme: VFDs, PF correction, additional sub-metering, scheduling controls, lighting retrofits.', 'Additional saving layer added. Platform tracks each intervention against its projected saving.', `${company} engineering + Saarv`],
          ['M7 — 90-Day Review', 'Month 5', 'Platform shows combined Phase 1 + 2 savings. Verify against projected run rate. Approve Phase 3 (industry-specific deep programme).', 'Combined run rate confirmed. Phase 3 business case presented with 90 days of measured baseline data.', 'Joint review'],
          ['M8 — Industry-Specific Programme', 'Months 6–12', 'Sustained operating discipline + technology programme targeting industry-best-practice intensity over 6 months.', 'Energy intensity begins measurable decline toward best-practice target by Month 9. Full target achieved by Month 12 with sustained discipline.', `${company} production team + Saarv quarterly review`],
          ['M9 — 12-Month Programme Review', 'Month 13', 'Full annual energy performance review. Total saving vs target. Capex recovery verified. Platform transitions to ongoing monitoring contract.', 'Target saving rate verified on Ergoniq platform. All capex recovered. Plant intensity at or below target benchmark.', 'Joint annual review + investor-ready performance report'],
        ]}
      />

      <SubHeader number="10.3" title="What Success Looks Like — Measurable Targets" />
      <DataTable
        headers={['Metric', 'Current (Baseline)', 'Month 3 Target', 'Month 12 Target', 'Measured By']}
        rows={[
          [
            'Annual electricity consumption',
            elecKwh ? `${fmtNum(elecKwh)} kWh` : '—',
            phase1AnnualSaving > 0 && elecKwh ? `${fmtNum(elecKwh - (phase1AnnualSaving / 6.5))} kWh` : '—',
            totalAnnualSaving > 0 && elecKwh ? `${fmtNum(elecKwh - (totalAnnualSaving / 6.5))} kWh` : '—',
            'Utility bill (kWh)',
          ],
          [
            'Annual electricity bill',
            annualBillINR ? fmtINR(annualBillINR) : '—',
            annualBillINR && phase1AnnualSaving ? fmtINR(annualBillINR - phase1AnnualSaving) : '—',
            annualBillINR && totalAnnualSaving ? fmtINR(annualBillINR - totalAnnualSaving) : '—',
            'Utility bill (₹)',
          ],
          [
            'Plant energy intensity',
            energyIntensity ? `${fmtNum(energyIntensity, 1)} kWh/MT` : '—',
            'Phase 1 reduction visible',
            'Best-practice target',
            'Ergoniq dashboard — per shift',
          ],
          [
            'Phase 1 saving (verified)',
            '—',
            phase1AnnualSaving > 0 ? `${fmtINR(phase1AnnualSaving)} run rate` : '—',
            phase1AnnualSaving > 0 ? `${fmtINR(phase1AnnualSaving)} sustained` : '—',
            'Ergoniq platform vs baseline',
          ],
          [
            'Full programme saving',
            '—',
            'Phase 1 visible',
            totalAnnualSaving > 0 ? `${fmtINR(totalAnnualSaving)} run rate` : '—',
            'Ergoniq platform vs baseline',
          ],
          [
            'GHG emissions reduction',
            ghgTotal ? `${fmtNum(ghgTotal, 1)} tCO₂e/yr baseline` : '—',
            totalTco2eAnnual ? `${fmtNum(totalTco2eAnnual * 0.3, 1)} tCO₂e avoided` : '—',
            totalTco2eAnnual ? `${fmtNum(totalTco2eAnnual, 1)} tCO₂e/yr avoided` : '—',
            'Ergoniq platform — emission factor × saved kWh',
          ],
          [
            'Capex deployed',
            '—',
            phase1Capex > 0 ? fmtINR(phase1Capex) : '—',
            totalCapex > 0 ? fmtINR(totalCapex) : '—',
            'Saarv project account',
          ],
        ]}
      />

      {/* CTA / closing card — surfaces the real headline numbers when
          they're available so the closing message isn't generic. */}
      <div style={{ backgroundColor: NAVY }} className="rounded p-6 my-8 text-center">
        <p className="text-yellow-300 font-bold text-xl mb-2">
          The Ergoniq Proposition to {company}
        </p>
        {totalAnnualSaving > 0 ? (
          <p className="text-white text-sm leading-relaxed">
            {siteName} currently spends <span className="font-semibold text-yellow-200">{annualBillINR ? fmtINR(annualBillINR) : '—'}</span> on
            electricity each year. The opportunity register identifies <span className="font-semibold text-yellow-200">{fmtINR(totalAnnualSaving)}/year</span> of recoverable
            saving against <span className="font-semibold text-yellow-200">{fmtINR(totalCapex)}</span> capex
            {totalAnnualSaving > 0 && <> — a blended payback of <span className="font-semibold text-yellow-200">{Math.max(1, Math.round(totalCapex / totalAnnualSaving * 12))} months</span></>}.
            Ergoniq makes that gap visible, measurable, and closeable — continuously,
            not once a year.
          </p>
        ) : (
          <p className="text-white text-sm leading-relaxed">
            The energy gap at {siteName} is visible, measurable, and closeable —
            continuously, not once a year. Run the Opportunity Engine V3 to
            quantify the saving and capex envelope for this site.
          </p>
        )}
      </div>

      {/* =================================================================
          APPENDIX A — ASSUMPTIONS REGISTER
          ================================================================= */}

      <SectionHeader title="Appendix A — Assumptions Register" />
      <P>
        All major assumptions are listed below with basis, sensitivity, and
        verification method. Sensitivity codes: H = High (material impact on
        opportunity total); M = Medium (moderate impact); L = Low (minor impact).
      </P>
      <DataTable
        headers={['Parameter', 'Value Used', 'Basis', 'Sensitivity', 'Verification']}
        rows={[
          ...(elecKwh ? [['Annual electricity', `${fmtNum(elecKwh)} kWh`, 'Sum of monthly bills', 'L', 'Bill confirmation at M0']] : []),
          ...(fuelsGj != null ? [['Annual fuels (thermal)', `${fmtNum(fuelsGj, 1)} GJ`, 'Sum of fuel records', 'L', 'Bill confirmation at M0']] : []),
          ...(productionMt != null ? [['Annual production', `${fmtNum(productionMt, 0)} MT`, 'Production records', 'M', '6 months of actuals at M0']] : []),
          ...(energyIntensity != null ? [['Energy intensity', `${fmtNum(energyIntensity, 1)} kWh/MT`, 'Calculated: total kWh ÷ production MT', 'M', 'Cross-validates entire energy balance']] : []),
          ...(baseline?.ghg?.grid_ef_used != null ? [['Grid emission factor', `${baseline.ghg.grid_ef_used} kgCO₂/kWh`, `${baseline.region || 'Regional'} grid factor`, 'L', 'Published utility/region factor']] : []),
          ...((baseline?.assumptions || []).map((a) => [
            a.parameter || a.name || '—',
            a.value || a.value_used || '—',
            a.basis || a.source || '—',
            a.sensitivity || 'M',
            a.verification || a.method || 'On-site measurement',
          ])),
          ['Tariff energy rate', '₹6.50 / kWh (default)', 'Platform default — override per study', 'L', 'Confirm from actual bill breakdown'],
          ...(oppCalcs.length > 0 ? [['Opportunity uncertainty band', '±15%', 'Industry standard for desktop pre-assessment', 'M', 'Replaced by on-site measurement at M2']] : []),
        ]}
      />

      {/* =================================================================
          APPENDIX B — KEY CALCULATIONS
          ================================================================= */}

      <SectionHeader title="Appendix B — Key Calculations" />
      <P>
        Every quantitative claim in this report is derived from one of the
        formulas below. Formulas are grouped by the report section that
        consumes them so an auditor can trace any figure back to its
        inputs. Values use the units stated in Appendix A — Assumptions
        Register; uncertainties carry through linearly unless marked
        otherwise.
      </P>
      <DataTable
        headers={['Quantity', 'Formula', 'Used in']}
        headAlign={['left', 'left', 'left']}
        rows={[
          // ---- Energy baseline (Section 2) -------------------------------
          [
            <span className="font-semibold text-gray-900">Annual electricity (kWh)</span>,
            <span className="font-mono text-xs">Σ monthly_bill.kwh</span>,
            '§2.4 Consolidated Energy Baseline',
          ],
          [
            <span className="font-semibold text-gray-900">Annual bill total (₹)</span>,
            <span className="font-mono text-xs">Σ monthly_bill.amount  (fallback: annual_kWh × ₹6.50)</span>,
            '§2.5 Consumption Profile · Executive Summary',
          ],
          [
            <span className="font-semibold text-gray-900">Energy intensity (kWh / MT)</span>,
            <span className="font-mono text-xs">total_kWh ÷ production_MT</span>,
            '§1.3 Capacity Cross-Check · Appendix A',
          ],
          // ---- SEU analysis (Section 3) ----------------------------------
          [
            <span className="font-semibold text-gray-900">Fuel → kWh equivalent</span>,
            <span className="font-mono text-xs">fuel_GJ × 277.778</span>,
            '§3.1 SEU Ranking (unifies electrical + thermal SEUs)',
          ],
          [
            <span className="font-semibold text-gray-900">SEU share of total (%)</span>,
            <span className="font-mono text-xs">(seu.annual_kWh ÷ Σ seu.annual_kWh) × 100</span>,
            '§3.1 SEU Ranking Table',
          ],
          // ---- Opportunity engine (Section 7) ----------------------------
          [
            <span className="font-semibold text-gray-900">Average capex (₹)</span>,
            <span className="font-mono text-xs">(capex_low + capex_high) ÷ 2</span>,
            '§7 Opportunity rows · Phase summaries',
          ],
          [
            <span className="font-semibold text-gray-900">Capex tier</span>,
            <span className="font-mono text-xs">
              avg ≤ ₹10k → No CAPEX · ≤ ₹5L → Low · ≤ ₹25L → Medium · &gt; ₹25L → High
            </span>,
            '§7 row classification (informational)',
          ],
          [
            <span className="font-semibold text-gray-900">Payback (months)</span>,
            <span className="font-mono text-xs">
              avg_capex = 0 → Immediate · annual_INR &gt; 0 → round((avg_capex ÷ annual_INR) × 12) · else → null
            </span>,
            '§7.1 / §7.2 / §7.3 row payback column',
          ],
          [
            <span className="font-semibold text-gray-900">Phase bucket</span>,
            <span className="font-mono text-xs">
              payback_months ≤ 3 → Phase 1 · 3 &lt; payback ≤ 12 → Phase 2 · &gt; 12 or null → Phase 3
            </span>,
            '§7.1 / §7.2 / §7.3 phase split',
          ],
          [
            <span className="font-semibold text-gray-900">Energy-charge saving (₹)</span>,
            <span className="font-mono text-xs">kWh_saving × tariff_energy_rate</span>,
            '§7 method callout · Phase row "Annual Saving"',
          ],
          [
            <span className="font-semibold text-gray-900">Demand-charge saving (₹)</span>,
            <span className="font-mono text-xs">avoided_kVA_demand × demand_charge_rate</span>,
            '§7 (added separately to relevant line items)',
          ],
          [
            <span className="font-semibold text-gray-900">Monthly kWh saved (display)</span>,
            <span className="font-mono text-xs">energy_savings_value ÷ 12</span>,
            '§7 row "kWh Saved/Month" column',
          ],
          // ---- Aggregates / consolidated (Section 7.4) -------------------
          [
            <span className="font-semibold text-gray-900">±15% uncertainty band</span>,
            <span className="font-mono text-xs">[annual × 0.85, annual × 1.15]</span>,
            '§7.4 Consolidated Summary',
          ],
          [
            <span className="font-semibold text-gray-900">Phase blended payback</span>,
            <span className="font-mono text-xs">round((phase.total_capex ÷ phase.total_annual_saving) × 12)</span>,
            '§7.4 Phase rows',
          ],
          [
            <span className="font-semibold text-gray-900">Programme blended payback</span>,
            <span className="font-mono text-xs">round((Σ phase.capex ÷ Σ phase.annual_saving) × 12)</span>,
            '§7.4 FULL PROGRAMME TOTAL',
          ],
          [
            <span className="font-semibold text-gray-900">Programme totals (₹, MT, kWh)</span>,
            <span className="font-mono text-xs">Σ over Phase 1 + Phase 2 + Phase 3</span>,
            '§7.4 · Executive Summary KPIs',
          ],
          // ---- GHG (Appendix A inputs, surfaced in §7.4) -----------------
          [
            <span className="font-semibold text-gray-900">GHG reduction (tCO₂e/yr)</span>,
            <span className="font-mono text-xs">kWh × grid_emission_factor ÷ 1000</span>,
            '§7.4 GHG row · Plant carbon footprint',
          ],
        ]}
      />
      <Callout kind="method" label="REPRODUCIBILITY">
        Every figure quoted in the body of this report can be reproduced by
        plugging the values from Appendix A into the formulas above. The
        on-site measurement campaign in §8 replaces simulated inputs with
        instrument-verified values; the formulas themselves do not change.
      </Callout>

      {/* Closing line */}
      <div className="border-t border-gray-200 mt-10 pt-4 text-center text-xs text-gray-500">
        <p className="italic text-emerald-700 font-semibold mb-1">
          This document is superseded by the Validated On-Site Study on completion of follow-up measurement campaigns.
        </p>
        <p>Saarv Solutions Pvt Ltd · Ergoniq Deep Energy Intelligence Platform</p>
        <p className="mt-1">Generated {generatedDate}</p>
      </div>
    </div>
  );
}
